import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import Typography from '@material-ui/core/Typography';

// UTILITY
import { Chip } from '@material-ui/core';
import { externalLink, getExternalUrlParams } from '@/utility/analytics';
import { updateUrl } from '@/utility/common';
import { getDuration, getPlaceholderUrl } from '@/utility/content';
import { formatDate, isFutureDate } from '@/utility/date';

// COMPONENTS
import ShowMoreText from '@/components/ShowMoreText';

// STYLES
import { Container } from './styles';

const EpisodeCard = ({
  data,
  seasonNumber,
  link,
  onWatchNowClick,
  analyticsData,
  analyticsLocation,
}) => {
  const [t] = useTranslation();
  const externalUrlParams = getExternalUrlParams();
  const href = !!link && updateUrl(link, externalUrlParams);
  const isUpcoming = !!data.air_date && isFutureDate(data.air_date);
  const isNewEpisode = !!data.is_current;

  function handleExternalLink() {
    const properties = {
      ...analyticsData,
      analyticsLocation,
      href,
      type: 'content link',
    };

    externalLink(properties);
  }

  function handleCardClick() {
    onWatchNowClick();
    handleExternalLink();
  }

  return (
    <Container className={clsx('episode-card', isUpcoming && 'episode-card--upcoming')}>
      <div className="episode-card__thumbnail thumbnail">
        {!!href && !isUpcoming && (
          <a
            className="episode-card__link"
            href={href}
            target="_blank"
            rel="noreferrer"
            onClick={handleCardClick}
          >
            {data.name}
          </a>
        )}
        <img
          src={data.image || getPlaceholderUrl({ title: data.name, width: 500, height: 281 })}
          alt={data.name}
        />
        {!!href && !isUpcoming && (
          <div className="play-fake">
            <i className="icon-play" />
          </div>
        )}
      </div>
      <div className="episode-card__main-info">
        <Typography className="episode-card__title cut-text" variant="h6" title={data.name}>
          {!!seasonNumber && `S${seasonNumber}`}{' '}
          {!!data.episode_number && `E${data.episode_number}`} - "{data.name}"
        </Typography>
        <ul className="episode-card__add-info">
          {!!data.air_date && (
            <Typography className="p4 m-b-5 text-regular" component="li">
              {formatDate(data.air_date, 'MMM d, yyyy')}
            </Typography>
          )}
          {!!data.runtime && (
            <Typography className="p4 m-b-5 text-regular" component="li">
              {getDuration(data.runtime || '')}
            </Typography>
          )}
        </ul>
        {isUpcoming && <Chip label={t('common.upcoming')} color="secondary" />}
        {isNewEpisode && <Chip label={t('common.newEpisode')} color="secondary" />}
        {!!data.overview && (
          <ShowMoreText openOnce collapsedHeight={50} showMoreText={t('buttons.seeMore')}>
            <Typography className="p4 text-regular">{data.overview}</Typography>
          </ShowMoreText>
        )}
      </div>
    </Container>
  );
};

EpisodeCard.defaultProps = {
  data: {},
  seasonNumber: '',
  link: '',
  onWatchNowClick: () => {},
  analyticsData: {},
  analyticsLocation: '',
};

EpisodeCard.propTypes = {
  data: PropTypes.object,
  seasonNumber: PropTypes.number,
  link: PropTypes.string,
  onWatchNowClick: PropTypes.func,
  analyticsData: PropTypes.object,
  analyticsLocation: PropTypes.string,
};

export default React.memo(EpisodeCard);
